import dayjs from 'dayjs';
import { createContext, ReactNode, useContext, useState } from 'react';
import * as React from 'react';
import { isAndroid, isIOS, isTablet } from 'react-device-detect';

import { REFUND_AMOUNT_NEEDS_HOLD } from '@/config/constants/user';
import {
  User,
  UserStatus,
  VerificationStatus,
} from '@/lib/apollo/types/gen/graphql';

interface UserState {
  user: User;
  setUser: (user: User) => void;
  isLoggedIn: boolean;
  isVerified: boolean;
  isWaiting: boolean;
  isMember: boolean;
  hasAgeRestriction: boolean;
  isTemporaryMember: boolean;
  isNeedSMSVerifyTop: boolean;
  isDisabledMember: boolean;
  isGuest: boolean;
  isViewWalletPage: boolean;
  isCouponLock: boolean;
  hasPendingBalance: boolean;
  genericCodeWs: number;
  browser: string;
  device: string;
  debugUser: boolean;
}

const UserContext = createContext<UserState>({} as UserState);

const judgeBrowser = () => {
  return 'web'; // アプリ版ができたら「app: アプリ」の判定ロジックを追加
};
const judgeDevice = () => {
  if (isAndroid) {
    return 'android';
  }
  if (isIOS) {
    return isTablet ? 'ipad' : 'iphone';
  }

  return 'pc';
};

interface UserProviderProps {
  initialUser: User;
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({
  initialUser,
  children,
}) => {
  const [value, setValue] = useState<UserState>({
    user: initialUser,
  } as UserState);

  value.setUser = (newUser: User) => {
    setValue(prev => {
      const newValue = { ...prev };
      newValue.user = { ...prev.user, ...newUser };
      return newValue;
    });
  };

  // ログイン状況
  value.isLoggedIn = value.user.userId !== undefined;
  // 本人確認ステータス
  value.isVerified =
    value.user.verificationStatus === VerificationStatus.Approved;
  value.isWaiting =
    value.user.verificationStatus === VerificationStatus.Waiting;
  value.hasAgeRestriction =
    value.user.verificationStatus === VerificationStatus.AgeRestriction;
  // 競輪会員ステータス
  value.isMember = value.user.status === UserStatus.Regular;
  value.isTemporaryMember = value.user.status === UserStatus.Temporary;
  value.isDisabledMember = value.user.status === UserStatus.Disabled;
  value.isGuest = !value.user.status || value.user.status === UserStatus.Guest;
  value.isNeedSMSVerifyTop =
    value.user.verificationLevel < 20 &&
    !!value.user.verified &&
    dayjs().isBefore(
      dayjs(value.user.verified)
        .startOf('day') // 承認日の00:00:00を取得
        .add(15, 'day'), // 15日後の00:00:00を期限とする
    );

  if (value.user.verified && value.user.created) {
    // ユーザ作成日か本人確認が、「02/01 〜 02/05」の場合は不要に更新
    const taregtSince = dayjs(import.meta.env.VITE_UPDATE_CAMPAIGN_RULE); // 2026-02-01
    const taregtUntil = dayjs(import.meta.env.VITE_UPDATE_CAMPAIGN_RULE_V2); // 2026-02-05
    if (taregtSince && taregtUntil) {
      if (
        taregtSince.unix() < value.user.verified.unix() &&
        value.user.verified.unix() < taregtUntil.unix()
      ) {
        value.isNeedSMSVerifyTop = false;
      }
      if (
        taregtSince.unix() < value.user.created.unix() &&
        value.user.created.unix() < taregtUntil.unix()
      ) {
        value.isNeedSMSVerifyTop = false;
      }
    }
  }

  // デバイス情報の収集
  value.browser = judgeBrowser();
  value.device = judgeDevice();

  // 入金ページの表示制御
  value.isViewWalletPage = false;
  if (value.user.verified) {
    if (
      value.user.verified.isBefore(
        dayjs(import.meta.env.VITE_VIEW_WALLET_BORDER),
      )
    ) {
      // 「本人確認日時 < 指定日付」の場合は表示する
      value.isViewWalletPage = true;
    } else {
      // 「本人確認日時+30日 < 現在日時」の場合は表示する
      value.isViewWalletPage = value.user.verified
        .add(30, 'day')
        .isBefore(dayjs());
    }
  }

  // クーポン入力画面の制御
  value.isCouponLock = false;
  if (value.user.couponLockPeriod) {
    value.isCouponLock = value.user.couponLockPeriod.isAfter(dayjs());
  }

  value.hasPendingBalance = value.user.wallet
    ? value.user.wallet.pendingBalance >= REFUND_AMOUNT_NEEDS_HOLD &&
      value.user.address?.addressId === undefined
    : false;

  value.genericCodeWs = 0;
  if (initialUser.wallet) {
    value.genericCodeWs = initialUser.wallet?.genericCodeWs;
  }

  value.debugUser = value.user.isPreviewFunctions;

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => useContext(UserContext);
